/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
/**
 * tileMeta — presentation metadata for an app tile.
 *
 * Given a {@link Tile}, returns the per-app icon, brand accent, and (optional)
 * i18n description key.
 *
 * NOTE (AI-LAUNCH-49): only `icon` is actually consumed, and only by
 * <AppCommandPalette>. <Tile> does NOT use this module — it has its own APP_ICONS
 * map — and nothing renders `accent` or `descKey`, so the tile descriptions in
 * en.json (`tiles.app.*.desc`) have never appeared on screen. The fields are left
 * in place pending a decision on whether tiles should show descriptions; deleting
 * them would quietly ratify the omission. This docstring previously claimed <Tile>
 * was a consumer, which it never was.
 *
 * Because real `app_id`/`name` values are
 * not known at build time, known apps are matched heuristically on a normalized
 * `name` + `route_prefix` string (lowercased). Matching is most-specific-first
 * so nested routes don't get swallowed by a generic family token (e.g.
 * `/orbit/sensors` matches "sensors" before "orbit"; `/msp/invoicing` matches
 * "invoic" before the generic "msp").
 *
 * Unknown apps fall back to a generic LayoutGrid icon with a deterministic
 * accent derived from a small string hash of `app_id`, so a given app always
 * renders the same color across loads.
 */
import {
  BarChart3,
  Building2,
  FileText,
  Gauge,
  LayoutGrid,
  Radar,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import type { Tile } from '../api';

export type TileAccent = 'lilac' | 'beach' | 'orange' | 'slate';

export interface TileMeta {
  icon: LucideIcon;
  accent: TileAccent;
  descKey?: string;
}

const FALLBACK_ACCENTS: TileAccent[] = ['lilac', 'beach', 'orange', 'slate'];

/**
 * Small deterministic string hash (djb2). Stable across loads so an unknown
 * app's fallback accent never flickers between renders.
 */
function hash(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return h >>> 0;
}

/**
 * Known-app matchers, ordered most-specific → generic. The first whose token
 * is contained in the normalized `name + route_prefix` string wins.
 */
const MATCHERS: { token: string; meta: TileMeta }[] = [
  { token: 'sensor', meta: { icon: Radar, accent: 'orange', descKey: 'tiles.app.sensors.desc' } },
  { token: 'invoic', meta: { icon: FileText, accent: 'lilac', descKey: 'tiles.app.invoicing.desc' } },
  { token: 'report', meta: { icon: BarChart3, accent: 'beach', descKey: 'tiles.app.reports.desc' } },
  { token: 'cfo', meta: { icon: BarChart3, accent: 'beach', descKey: 'tiles.app.reports.desc' } },
  { token: 'admin', meta: { icon: Settings, accent: 'slate', descKey: 'tiles.app.admin.desc' } },
  { token: 'setting', meta: { icon: Settings, accent: 'slate', descKey: 'tiles.app.admin.desc' } },
  { token: 'orbit', meta: { icon: Gauge, accent: 'beach', descKey: 'tiles.app.orbit.desc' } },
  { token: 'msp', meta: { icon: Building2, accent: 'lilac', descKey: 'tiles.app.msp.desc' } },
];

export function tileMeta(tile: Tile): TileMeta {
  const key = `${tile.name} ${tile.route_prefix}`.toLowerCase();

  for (const { token, meta } of MATCHERS) {
    if (key.includes(token)) {
      return meta;
    }
  }

  return {
    icon: LayoutGrid,
    accent: FALLBACK_ACCENTS[hash(tile.app_id) % FALLBACK_ACCENTS.length],
    descKey: undefined,
  };
}
